import * as React from "react"
import NumberFormat from "react-number-format"
import { formatCentsAsUsd, parseDollarsToCents } from "../util/money"
import { format, parseISO } from "date-fns"

interface Props {
  onlyAllowInsurancePayments: boolean
  achIncentiveForSource: AchIncentive | undefined
  attendanceSummaries: AttendanceSummary[]
  overdueMilestones: Milestone[]
  suggestedMilestones: Milestone[]
  oneOrMoreParticipantsHasPendingInsurance: boolean
}

interface AchIncentive {
  type: AchIncentiveType
  percentage: number
}

export enum AchIncentiveType {
  CardFee,
  AchDiscount,
}

interface AttendanceSummary {
  person_name: string
  participation_id: number
  net_paid: number
  trip_total: number
  insurance_plan_cost: number
}

interface Milestone {
  remaining_balance: number
  participant_breakdown: ParticipantBreakdown[]
  date: string
}

interface ParticipantBreakdown {
  participation: { id: number }
  total_due: number
  remaining_balance: number
}

interface State {
  paymentType: "overdue" | "suggested" | "custom" | "insurance" | undefined
  participationPaymentLines: ParticipationPaymentLine[]
}

interface ParticipationPaymentLine {
  id: number
  name: string
  amount: number
}

export class ParticipationPaymentBreakdownFormInputs extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = this.initializeState(props)
  }

  componentDidMount() {
    if (this.props.onlyAllowInsurancePayments) {
      this.setParticipationPaymentLinesToMatchInsurance()
    } else {
      this.setParticipationPaymentLinesToZero()
    }
  }

  setToSuggested = () => {
    this.setParticipationPaymentLinesToMatchMilestones(this.props.suggestedMilestones)
    this.setState({ paymentType: "suggested" })
  }

  setToCustom = () => {
    this.setParticipationPaymentLinesToZero()
    this.setState({ paymentType: "custom" })
  }

  setToOverdue = () => {
    this.setParticipationPaymentLinesToMatchMilestones(this.props.overdueMilestones)
    this.setState({ paymentType: "overdue" })
  }

  setToInsurance = () => {
    this.setParticipationPaymentLinesToMatchInsurance()
    this.setState({ paymentType: "insurance" })
  }

  initializeState(props: Props): State {
    const participationPaymentLines: ParticipationPaymentLine[] = []
    props.attendanceSummaries.forEach((as) => {
      participationPaymentLines.push({
        id: as.participation_id,
        name: as.person_name,
        amount: 0,
      })
    })

    const maybeDefaultPaymentType = props.onlyAllowInsurancePayments ? "insurance" : undefined
    return { paymentType: maybeDefaultPaymentType, participationPaymentLines: participationPaymentLines }
  }

  hasOverdueMilestones = () => this.props.overdueMilestones.length > 0

  render() {
    return (
      <div className="grid gap-block-4" key="1">
        <div className="italic text-gray-700">Choose the amount you would like to pay</div>
        {this.props.onlyAllowInsurancePayments ? this.renderInsuranceOnlyOption() : this.renderStandardPaymentOptions()}

        {this.props.attendanceSummaries.map((as: AttendanceSummary) => (
          <input
            type="hidden"
            name={`participation_amounts[${as.participation_id}]`}
            value={this.getFormattedParticipationAmount(as.participation_id)}
            key={as.participation_id}
          />
        ))}
      </div>
    )
  }

  renderStandardPaymentOptions() {
    return (
      <>
        {this.hasOverdueMilestones() && (
          <OverdueAmountRadioButton
            checked={this.state.paymentType === "overdue"}
            onChange={(_e) => this.setToOverdue()}
            overdueMilestones={this.props.overdueMilestones}
            participationPaymentLines={this.state.participationPaymentLines}
            achIncentiveForSource={this.props.achIncentiveForSource}
            oneOrMoreParticipantsHasPendingInsurance={this.props.oneOrMoreParticipantsHasPendingInsurance}
          />
        )}
        <SuggestedAmountRadioButton
          checked={this.state.paymentType === "suggested"}
          onChange={(_e) => this.setToSuggested()}
          suggestedMilestones={this.props.suggestedMilestones}
          participationPaymentLines={this.state.participationPaymentLines}
          achIncentiveForSource={this.props.achIncentiveForSource}
          oneOrMoreParticipantsHasPendingInsurance={this.props.oneOrMoreParticipantsHasPendingInsurance}
        />
        <CustomAmountRadioButton
          checked={this.state.paymentType === "custom"}
          onChange={(_e) => this.setToCustom()}
          attendanceSummaries={this.props.attendanceSummaries}
          getFormattedParticipationAmount={this.getFormattedParticipationAmount}
          setParticipationAmount={this.setParticipationAmount}
          achIncentiveForSource={this.props.achIncentiveForSource}
          participationPaymentLines={this.state.participationPaymentLines}
        />
      </>
    )
  }

  renderInsuranceOnlyOption() {
    return (
      <InsurancePremiumsRadioButton
        checked={this.state.paymentType === "insurance"}
        onChange={(_e) => this.setToInsurance()}
        attendanceSummaries={this.props.attendanceSummaries}
        participationPaymentLines={this.state.participationPaymentLines}
        achIncentiveForSource={this.props.achIncentiveForSource}
      />
    )
  }

  setParticipationAmount = (partId: number, newAmountCents?: number): void => {
    let states = this.state.participationPaymentLines
    const idx = states.findIndex((target) => target.id === partId)
    if (newAmountCents && newAmountCents > 0) {
      states[idx] = { ...states[idx], amount: newAmountCents }
    } else {
      states[idx] = { ...states[idx], amount: 0 }
    }
    this.setState({ participationPaymentLines: states })
  }

  getFormattedParticipationAmount = (partId: number): string => {
    let states = this.state.participationPaymentLines
    const idx = states.findIndex((target) => target.id === partId)
    const amountCents: number = states[idx].amount
    return formatCentsAsUsd(amountCents)
  }

  setParticipationPaymentLinesToMatchMilestones = (milestones: Milestone[]): void => {
    const amountsByPartId = new Map()

    milestones.forEach((milestone: Milestone) => {
      milestone.participant_breakdown.forEach((breakdown: ParticipantBreakdown) => {
        const prevAmount = amountsByPartId.get(breakdown.participation.id) || 0
        const newAmount = prevAmount + breakdown.remaining_balance
        amountsByPartId.set(breakdown.participation.id, newAmount)
      })
    })

    amountsByPartId.forEach((amount, partId) => {
      this.setParticipationAmount(partId, amount)
    })
  }

  setParticipationPaymentLinesToZero = (): void => {
    this.props.attendanceSummaries.forEach((summary) => {
      this.setParticipationAmount(summary.participation_id, 0)
    })
  }

  setParticipationPaymentLinesToMatchInsurance = (): void => {
    this.props.attendanceSummaries.forEach((summary) => {
      const insuranceCost = summary.insurance_plan_cost
      const netPaid = summary.net_paid
      const insurancePayable = Math.max(insuranceCost - netPaid, 0)
      this.setParticipationAmount(summary.participation_id, insurancePayable)
    })
  }
}

interface OverdueAmountRadioButtonProps {
  checked: boolean
  onChange: any
  overdueMilestones: Milestone[]
  participationPaymentLines: ParticipationPaymentLine[]
  achIncentiveForSource: AchIncentive | undefined
  oneOrMoreParticipantsHasPendingInsurance: boolean
}

const OverdueAmountRadioButton = (props: OverdueAmountRadioButtonProps) => {
  const overdueTotalCentsBeforeFees: number = props.overdueMilestones.reduce(
    (acc, milestone) => acc + milestone.remaining_balance,
    0
  )

  const overdueTotalCents =
    overdueTotalCentsBeforeFees + achIncentiveAmount(props.achIncentiveForSource, overdueTotalCentsBeforeFees)
  return (
    <div>
      <div className="grid grid-flow-col justify-start gap-inline-2 align-middle  ">
        <input
          className="payment-split-radio mt-2"
          id="overdue_amounts_split"
          type="radio"
          checked={props.checked}
          data-role="suggested-amount-split-radio"
          onChange={props.onChange}
        />
        <label className="flex items-center text-lg" htmlFor="overdue_amounts_split">
          <span>
            <span className="mr-1">Overdue Amount Only</span>
            {props.oneOrMoreParticipantsHasPendingInsurance && (
              <span className="text-sm2 text-gray-800">(Includes Trip Protection)</span>
            )}
          </span>
          <span className="ml-1 p-1 sm:p-2 bg-gray-100 border-gray-300">{formatCentsAsUsd(overdueTotalCents)}</span>
        </label>
      </div>

      {props.checked && (
        <div>
          <MilestoneBreakdown
            milestones={props.overdueMilestones}
            participationPaymentLines={props.participationPaymentLines}
          />
          <FeeOrDiscountBreakdown
            achIncentiveForSource={props.achIncentiveForSource}
            totalCentsBeforeFees={overdueTotalCentsBeforeFees}
          />
        </div>
      )}
    </div>
  )
}

interface SuggestedAmountRadioButtonProps {
  checked: boolean
  onChange: any
  suggestedMilestones: Milestone[]
  participationPaymentLines: ParticipationPaymentLine[]
  achIncentiveForSource: AchIncentive | undefined
  oneOrMoreParticipantsHasPendingInsurance: boolean
}

const SuggestedAmountRadioButton = (props: SuggestedAmountRadioButtonProps) => {
  const suggestedTotalCentsBeforeFees: number = props.suggestedMilestones.reduce(
    (acc, milestone) => acc + milestone.remaining_balance,
    0
  )

  const suggestedTotalCents =
    suggestedTotalCentsBeforeFees + achIncentiveAmount(props.achIncentiveForSource, suggestedTotalCentsBeforeFees)

  return (
    <div>
      <div className="flex items-center justify-start gap-inline-2">
        <input
          className="payment-split-radio"
          id="suggested_amounts_split"
          type="radio"
          checked={props.checked}
          data-role="suggested-amount-split-radio"
          onChange={props.onChange}
        />
        <label className="flex items-center text-lg" htmlFor="suggested_amounts_split">
          <span className="flex flex-wrap gap-inline-1 items-center">
            <span>Suggested Amount</span>
            {props.oneOrMoreParticipantsHasPendingInsurance && (
              <span className="text-sm2 text-gray-800">(Includes Trip Protection)</span>
            )}
          </span>
          <span className="ml-4 p-1 sm:p-2 bg-gray-100 border-gray-300">{formatCentsAsUsd(suggestedTotalCents)}</span>
        </label>
      </div>

      {props.checked && (
        <div>
          <MilestoneBreakdown
            milestones={props.suggestedMilestones}
            participationPaymentLines={props.participationPaymentLines}
          />
          <FeeOrDiscountBreakdown
            achIncentiveForSource={props.achIncentiveForSource}
            totalCentsBeforeFees={suggestedTotalCentsBeforeFees}
          />
        </div>
      )}
    </div>
  )
}

interface CustomAmountRadioButtonProps {
  checked: boolean
  onChange: any

  attendanceSummaries: AttendanceSummary[]
  getFormattedParticipationAmount: any
  setParticipationAmount: any

  participationPaymentLines: ParticipationPaymentLine[]
  achIncentiveForSource: AchIncentive | undefined
}

const CustomAmountRadioButton = (props: CustomAmountRadioButtonProps) => {
  const totalCentsBeforeFees = props.participationPaymentLines.map((pa) => pa.amount).reduce((acc, current) => acc + current)

  return (
    <div className="">
      <div className="grid grid-flow-col justify-start gap-inline-2 align-middle  ">
        <input
          className="payment-split-radio mt-2"
          id="custom_amounts_split"
          type="radio"
          checked={props.checked}
          data-role="custom-split-radio"
          onChange={props.onChange}
        />
        <label className="text-lg py-2" htmlFor="custom_amounts_split">
          Custom Amount
        </label>
      </div>

      {props.checked &&
        props.attendanceSummaries.map((as: AttendanceSummary) => (
          <div
            data-role={`participation-split-input`}
            data-participation-id={as.participation_id}
            key={as.participation_id}
            className="grid sm:grid-cols-12 gap-inline-2 mx-8 sm:mx-2 my-3 items-center">
            <div className="sm:col-span-2">
              <NumberFormat
                className="text-right"
                thousandSeparator={true}
                prefix={"$"}
                value={props.getFormattedParticipationAmount(as.participation_id)}
                onValueChange={(value) => {
                  let newAmountCents: number | undefined = parseDollarsToCents(value.formattedValue)
                  props.setParticipationAmount(as.participation_id, newAmountCents)
                }}
                data-testid={`amount-input[${as.participation_id}]`}
                data-role={"amount-input"}
                placeholder={"$"}
              />
            </div>
            <div className="sm:col-span-10">
              <span>of {formatCentsAsUsd(Math.max(as.trip_total - as.net_paid, 0))} left </span>
              for
              <span className="font-bold"> {as.person_name}</span>
            </div>
          </div>
        ))}
      {props.checked && (
        <div>
          <FeeOrDiscountBreakdown
            achIncentiveForSource={props.achIncentiveForSource}
            totalCentsBeforeFees={totalCentsBeforeFees}
          />
        </div>
      )}
    </div>
  )
}

interface InsurancePremiumsRadioButtonProps {
  checked: boolean
  onChange: any
  attendanceSummaries: AttendanceSummary[]
  participationPaymentLines: ParticipationPaymentLine[]
  achIncentiveForSource: AchIncentive | undefined
}

const InsurancePremiumsRadioButton = (props: InsurancePremiumsRadioButtonProps) => {
  const totalCentsBeforeFees: number = props.participationPaymentLines.reduce((acc, ppl) => acc + ppl.amount, 0)
  const totalCents = totalCentsBeforeFees + achIncentiveAmount(props.achIncentiveForSource, totalCentsBeforeFees)

  return (
    <div>
      <div className="grid grid-flow-col justify-start gap-inline-2 align-middle  ">
        <input
          className="payment-split-radio mt-2"
          id="insurance_costs_radio"
          type="radio"
          checked={props.checked}
          onChange={props.onChange}
          data-role="insurance-costs-split-radio"
        />

        <label className="flex items-center text-lg" htmlFor="insurance_costs_radio">
          Trip Protection Costs
          {props.checked && (
            <span className="ml-1 p-1 sm:p-2 bg-gray-100 border-gray-300">{formatCentsAsUsd(totalCents)}</span>
          )}
        </label>
      </div>

      {props.checked && (
        <div>
          <InsuranceCostBreakdown participationPaymentLines={props.participationPaymentLines} />
          <FeeOrDiscountBreakdown
            achIncentiveForSource={props.achIncentiveForSource}
            totalCentsBeforeFees={totalCentsBeforeFees}
          />
        </div>
      )}
    </div>
  )
}

interface MilestoneBreakdownProps {
  milestones: Milestone[]
  participationPaymentLines: ParticipationPaymentLine[]
}

const MilestoneBreakdown = (props: MilestoneBreakdownProps) => {
  return (
    <div className="sm:flex mx-8 my-2 pl-18" data-role="payment-breakdown-preview" key="2">
      <div className="">
        <h3 className="font-semibold text-md mb-1">Payment Due Dates</h3>

        {props.milestones.map((m) => (
          <div className="py-1" key={m.date}>
            <span className="">{formatCentsAsUsd(m.remaining_balance)}</span>
            <span className=""> {m["deposit?"] ? "due for deposit on" : "due on"} </span>
            <span className="">{format(parseISO(m.date), "MMM dd yyyy")}</span>
          </div>
        ))}
      </div>

      {props.participationPaymentLines.length > 1 && (
        <div className="sm:ml-12 sm:pl-8 mt-4 sm:mt-0 sm:border-l">
          <h3 className="font-semibold text-md mb-1">Per Passenger Breakdown</h3>
          <div>
            {props.participationPaymentLines.map((participationAmount) => (
              <div className="py-1" key={participationAmount.id}>
                <span className=""> {formatCentsAsUsd(participationAmount.amount)} </span>
                <span className="">for {participationAmount.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface InsuranceCostBreakdownProps {
  participationPaymentLines: ParticipationPaymentLine[]
}

const InsuranceCostBreakdown = (props: InsuranceCostBreakdownProps) => {
  return (
    <div className="sm:flex mx-8 my-2 pl-18" data-role="payment-breakdown-preview" key="2">
      {props.participationPaymentLines.length > 1 && (
        <div className="sm:ml-12 sm:pl-8 mt-4 sm:mt-0 sm:border-l">
          <h3 className="font-semibold text-md mb-1">Per Passenger Breakdown</h3>
          <div>
            {props.participationPaymentLines.map((participationAmount) => (
              <div className="py-1" key={participationAmount.id}>
                <span className=""> {formatCentsAsUsd(participationAmount.amount)} </span>
                <span className="">for {participationAmount.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface FeeOrDiscountBreakdownProps {
  achIncentiveForSource: AchIncentive | undefined
  totalCentsBeforeFees: number
}

const FeeOrDiscountBreakdown = (props: FeeOrDiscountBreakdownProps) => {
  return (
    <div className="grid gap-block-1 mx-8 my-4 font-bold">
      {props.achIncentiveForSource != undefined && (
        <div className="" data-role="preview-breakdown-entry" key="fees">
          <span className="">{formattedFeeOrDiscount(props.achIncentiveForSource, props.totalCentsBeforeFees)} </span>
          <span className="">{formatAchIncentiveName(props.achIncentiveForSource)}</span>
        </div>
      )}
    </div>
  )
}

const formattedFeeOrDiscount = (achIncentiveForSource: AchIncentive | undefined, totalCentsBeforeFees: number) => {
  let incentiveAmount = achIncentiveAmount(achIncentiveForSource, totalCentsBeforeFees)

  if (incentiveAmount < 0) {
    return `- ${formatCentsAsUsd(Math.abs(incentiveAmount))}`
  } else {
    return `+ ${formatCentsAsUsd(Math.abs(incentiveAmount))}`
  }
}

const achIncentiveAmount = (achIncentiveForSource: AchIncentive | undefined, amount: number): number => {
  if (achIncentiveForSource != undefined) {
    return (amount * achIncentiveForSource.percentage) / 100
  } else {
    return 0
  }
}

const formatAchIncentiveName = (achIncentiveForSource: AchIncentive | undefined): string => {
  if (achIncentiveForSource.type === AchIncentiveType.CardFee) {
    return "Convenience Fees"
  } else {
    return "ACH Discount"
  }
}
